import type { Condition } from '@medplum/fhirtypes';

import { Grid, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import type { FhirView } from '../../core/view-contract';
import { resourceDisplayName } from '../accessors';
import { formatFhirDate } from '../observation/format';

/**
 * The exported Condition component. Problem-list surfaces render it in lists,
 * so the row is the primary variant.
 */
export const ConditionView: FhirView<Condition> = (props) => {
  if (props.ctx.inList) return <ConditionRowView {...props} />;
  return <ConditionReadView {...props} />;
};

function clinicalStatusCode(data: Condition): string | undefined {
  return data.clinicalStatus?.coding?.[0]?.code;
}

function conditionDateText(data: Condition): string | undefined {
  const date = formatFhirDate(data.onsetDateTime ?? data.recordedDate);
  if (!date) return undefined;
  return data.onsetDateTime ? `Onset ${date}` : `Recorded ${date}`;
}

/** Problem-list row: name, clinical status, onset/recorded date. */
const ConditionRowView: FhirView<Condition> = ({ data }) => {
  const date = conditionDateText(data);
  return (
    <Grid
      data-view="condition-row"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <Stack direction="horizontal" gap={2} align="center" className="min-w-0">
        <Text inline size="sm" weight="regular" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        <FhirStatusTag status={clinicalStatusCode(data)} />
      </Stack>
      {date && (
        <Text inline size="xs" color="tertiary">
          {date}
        </Text>
      )}
    </Grid>
  );
};

/** Standalone card: the condition is the page's subject. */
const ConditionReadView: FhirView<Condition> = ({ data, ctx }) => {
  const date = conditionDateText(data);
  const verification = data.verificationStatus?.coding?.[0]?.code;
  return (
    <div
      data-view="condition-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          {resourceDisplayName(data)}
        </Text>
        <FhirStatusTag status={clinicalStatusCode(data)} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {date && (
          <Text inline size="xs" color="tertiary">
            {date}
          </Text>
        )}
        {verification && verification !== 'confirmed' && (
          <Text inline size="xs" color="tertiary">
            {verification.replace(/-/g, ' ')}
          </Text>
        )}
      </Stack>
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
